/**
 * Shared wheel types for the Spoil Engine application
 * This app allows users to create and share customizable wheels for various purposes
 */
import { Timestamp } from 'firebase/firestore';

/**
 * Enum representing the possible statuses of a shared wheel
 */
export enum ShareStatus {
  ACTIVE = 'active',
  EXPIRED = 'expired',
  INACTIVE = 'inactive',
  MAX_SPINS_REACHED = 'max_spins_reached',
  INVALID = 'invalid',
}

export enum WheelEntryType {
  TEXT = 'text',
  REWARD = 'reward', // Rewards for completing tasks
  TRIBUTE = 'tribute', // Contributions or gifts
  CHALLENGE = 'challenge', // Challenges to complete
}

export enum WheelTheme {
  DEFAULT = 'default',
  PASTEL = 'pastel',
  DARK = 'dark',
  NEON = 'neon',
  GOLD = 'gold',
}

export interface WheelSettings {
  allowDuplicateSpins: boolean;
  wheelStyle?: string;
  customCss?: string;
}

export interface WheelEntry {
  id?: string;
  text: string;
  type: WheelEntryType | string;
  color?: string;
  emoji?: string;
  weight: number;
  metadata?: Record<string, string | number | boolean | null>;
}

/**
 * Interface for configuring shared wheel settings
 * Control how users interact with your shared wheel
 */
export interface ShareSettings {
  maxSpins: number | null; // Maximum number of spins allowed (null for unlimited)
  expiresAt: Timestamp | Date | null; // When the share expires (null for no expiration)
  accessCode: string | null; // Optional access code for additional security
  isActive: boolean; // Whether the share is currently active
  createdAt: Timestamp | Date; // When the share was created
  allowedRoles: string[]; // Roles that can access this share
  shareId: string; // Unique identifier for this specific share
}

/**
 * Interface representing a spin performed on a shared wheel
 * Track how users interact with your shared wheel
 */
export interface SharedWheelSpin {
  id: string; // Unique identifier for the spin
  entryId: string; // ID of the wheel entry that was landed on
  entryText: string; // Text of the wheel entry
  timestamp: Timestamp | Date; // When the spin occurred
  userId?: string | null; // User ID of who performed the spin (null for unauthenticated users)
}

/**
 * Interface representing a shared instance of a wheel
 * Share with others and track their interactions with the wheel
 */
export interface SharedWheel {
  parentWheelId: string; // ID of the original wheel
  shareId: string; // Unique identifier matching the one in ShareSettings
  ownerId: string; // User ID of the wheel owner
  recipientId?: string; // User ID of the recipient (if any)
  settings: ShareSettings; // The share settings for this instance
  spins: SharedWheelSpin[]; // Array of spins performed on this shared wheel
  currentSpinCount: number; // Current number of spins used
}

export interface Wheel {
  id: string;
  name: string;
  description?: string;
  entries: WheelEntry[];
  theme: WheelTheme | string;
  custom_colors?: string[];
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  createdBy: string | null;
  isPublic: boolean;
  category?: string;
  difficulty?: number;
  spinCount: number;
  favorites: number;
  tags?: string[];
  wheelSettings: WheelSettings;
  storageType?: 'local' | 'cloud'; // Indicates whether the wheel is stored locally or in the cloud
  shareSettings?: ShareSettings[]; // Array of share settings for different share instances
}

/**
 * Format a wheel's creation date to a human-readable string
 * @param wheel The wheel object containing a created_at property
 * @returns A formatted date string or 'Unknown date' if not available
 */
export const formatWheelCreationDate = (wheel: Wheel): string => {
  if (!wheel.createdAt) return 'Unknown date';

  try {
    // Check if it's a serverTimestamp object (has _methodName property)
    const createdAtAny = wheel.createdAt as any;
    if (
      createdAtAny &&
      typeof createdAtAny === 'object' &&
      createdAtAny._methodName === 'serverTimestamp'
    ) {
      return 'Recently created';
    }

    // Handle both Firestore Timestamp and Date objects
    if (wheel.createdAt instanceof Date) {
      return wheel.createdAt.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }

    // Check if it's a Firestore Timestamp (has toDate method)
    if (typeof createdAtAny === 'object' && typeof createdAtAny.toDate === 'function') {
      const date = createdAtAny.toDate();
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    }

    // Check if it's a string (from localStorage)
    if (typeof wheel.createdAt === 'string') {
      try {
        const date = new Date(wheel.createdAt);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          });
        }
      } catch (e) {
        console.error('Error parsing date string:', e);
      }
    }

    return 'Unknown date format';
  } catch (error) {
    console.error('Error formatting wheel creation date:', error);
    return 'Invalid date';
  }
};

/**
 * Convert a wheel from the old format to the Firestore format
 * @param oldWheel The wheel in the old format
 * @returns A wheel in the Firestore format
 */
export const convertToFirestoreWheel = (oldWheel: any): Partial<Wheel> => {
  return {
    name: oldWheel.name,
    description: '',
    entries: oldWheel.entries.map((entry: any) => ({
      text: entry.text,
      type: entry.type || WheelEntryType.TEXT,
      color: entry.color,
      emoji: entry.emoji,
      weight: entry.weight || 1,
      metadata: entry.metadata || {},
    })),
    theme: oldWheel.theme || WheelTheme.DEFAULT,
    custom_colors: oldWheel.custom_colors || [],
    isPublic: false,
    category: 'custom',
    difficulty: 1,
    tags: [],
    wheelSettings: {
      allowDuplicateSpins: true,
      wheelStyle: 'default',
    },
  };
};
